import { warn } from './warn';

import type { CurrentUserFetchResponse } from '@api/auth/api.auth.types';

/**
 * Системные свойства профиля вендора — заполняются только этими ключами,
 * произвольное имя завело бы вместо них обычное пользовательское свойство.
 *
 * @see https://developers.carrotquest.io/libs/js/identify/
 */
const NAME_KEY = '$name';
const EMAIL_KEY = '$email';
const PHONE_KEY = '$phone';

const buildContactOperations = (user: CurrentUserFetchResponse) => {
  const fullName =
    user.full_name || `${user.first_name} ${user.last_name}`.trim();

  const contacts: [string, string][] = [
    [NAME_KEY, fullName],
    [EMAIL_KEY, user.email],
    [PHONE_KEY, user.phone_number],
  ];

  // Пустое значение вендор запишет как есть и затрёт то, что оператор
  // поддержки уже дописал в профиль руками, — такие ключи пропускаем.
  return contacts
    .filter(([, value]) => value)
    .map(([key, value]) => ({ op: 'update_or_create', key, value }));
};

/**
 * Пишет контакты пользователя в профиль CarrotQuest.
 *
 * Значения берутся из ответа API, а не из полей формы регистрации: до AI-104
 * их вычитывал обработчик сабмита в capture-фазе — то есть до валидации, до
 * отметки о согласии на обработку персональных данных и до ответа сервера,
 * так что контакты уходили вендору даже с неудачной попытки.
 *
 * Согласие на обработку персональных данных проверяется через
 * `data_complete`. Прямого признака согласия бэк не отдаёт, но профиль
 * помечается заполненным только после одной из двух форм — регистрации
 * (`signUpFormSchema`) или дозаполнения (`completePersonalDataFormSchema`), —
 * а обе требуют `personalDataAgreementSchema`. Наличия пользователя мало:
 * аккаунт может существовать до согласия, и тогда роутер уводит его на
 * `/auth/complete-personal-data` (`routes/auth/index.tsx:18`), где галочка
 * ставится заново.
 *
 * Вызывать только после `carrotQuestAuth` — иначе контакты осядут
 * в анонимном профиле, что противоречит контракту вендора.
 *
 * @example
 * if (carrotQuestAuth(user, user.user_carrot_quest_hash)) {
 *   carrotQuestIdentifyUser(user);
 * }
 */
export const carrotQuestIdentifyUser = (user: CurrentUserFetchResponse) => {
  if (!user.data_complete) {
    return;
  }

  const operations = buildContactOperations(user);

  if (operations.length === 0) {
    return;
  }

  try {
    globalThis.carrotquest?.identify(operations);
  } catch (error) {
    warn(`Identify failed: ${error}`);
  }
};
